import React, { useState } from 'react';
import {
  CheckCircleIcon,
  ChevronDoubleRightIcon,
  EyeIcon,
  XMarkIcon,
  ChartBarIcon,
  UserGroupIcon,
  TableCellsIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

export default function TaskTrainingHelp() {
  const [activePopup, setActivePopup] = useState(null);

  const showPopup = (popupId) => {
    setActivePopup(popupId);
  };

  const closePopup = () => {
    setActivePopup(null);
  };

  const Popup = ({ title, description, isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 relative">
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl px-6 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">{title}</h3>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>
          <div className="p-6">
            <p className="text-gray-700 leading-relaxed">{description}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <UserGroupIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Bottleneck Analysis</h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Comprehensive process visualization and training system for managers to
            analyze workflows, identify issues, and optimize team performance.
          </p>
        </div>

        {/* System Overview */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center">
              <UserGroupIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Task Training Overview</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4">
                  <UserGroupIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Process Directory</h3>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Browse and select from organized process workflows to analyze
                  specific business operations and training requirements.
                </p>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                  <ChartBarIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Issue Analytics</h3>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  Visualize issue patterns and bottlenecks across different process
                  steps using interactive charts and data analysis tools.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mb-4">
                  <ArrowRightIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Process Flow</h3>
                <p className="text-purple-700 text-sm leading-relaxed">
                  Interactive flowchart visualization showing the complete process
                  workflow with substeps, dependencies, and issue indicators.
                </p>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-red-50 rounded-2xl p-6 border border-pink-200">
                <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-red-600 rounded-xl flex items-center justify-center mb-4">
                  <TableCellsIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Detailed Analysis</h3>
                <p className="text-pink-700 text-sm leading-relaxed">
                  Drill down into specific substeps with detailed issue tracking,
                  help responses, and performance metrics analysis.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Interactive Demo */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6">
            <div className="flex items-center">
              <EyeIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Interactive Process Viewer Demo</h2>
            </div>
          </div>

          <div className="p-8">
            {/* Directory Demo */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Process Directory Navigation</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between bg-white rounded-lg p-4 shadow-sm">
                  <div>
                    <h4 className="font-semibold text-gray-900">Customer Service Process</h4>
                    <p className="text-sm text-gray-600">Handle customer inquiries and support</p>
                    <p className="text-xs text-gray-500">Assigned to: Sarah Johnson</p>
                  </div>
                  <button
                    onClick={() => showPopup('select-process')}
                    className="cursor-pointer transition-colors hover:bg-emerald-700 bg-emerald-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
                  >
                    <span>Select Process</span>
                    <ChevronDoubleRightIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="flex items-center justify-between bg-white rounded-lg p-4 shadow-sm">
                  <div>
                    <h4 className="font-semibold text-gray-900">Inventory Management</h4>
                    <p className="text-sm text-gray-600">Stock tracking and ordering system</p>
                    <p className="text-xs text-gray-500">Assigned to: Mike Davis</p>
                  </div>
                  <button
                    onClick={() => showPopup('select-process')}
                    className="cursor-pointer transition-colors hover:bg-emerald-700 bg-emerald-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
                  >
                    <span>Select Process</span>
                    <ChevronDoubleRightIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Analytics Demo */}
            <div className="bg-black rounded-xl p-6 mb-8">
              <h3 className="text-xl font-bold text-white mb-4">Issue Analytics Dashboard</h3>
              <div className="bg-gray-800 rounded-lg p-4 mb-4">
                <h4 className="text-white font-semibold mb-2">Issue Count by Task Step</h4>
                <div className="flex items-center justify-center h-32 bg-gray-700 rounded">
                  <button
                    onClick={() => showPopup('issue-chart')}
                    className="cursor-pointer transition-colors hover:bg-blue-600 bg-blue-500 text-white px-6 py-3 rounded-lg flex items-center space-x-2"
                  >
                    <ChartBarIcon className="h-6 w-6" />
                    <span>Interactive Issue Chart</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Flow Visualization Demo */}
            <div className="bg-black rounded-xl p-6 mb-8">
              <h3 className="text-xl font-bold text-white mb-4">Process Flow Visualization</h3>
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg p-6">
                <div className="flex items-center justify-center h-40 bg-gradient-to-r from-blue-900 to-purple-900 rounded-lg relative">
                  <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm">
                    Bottleneck Detected
                  </div>
                  <button
                    onClick={() => showPopup('process-flow')}
                    className="cursor-pointer transition-colors hover:bg-indigo-600 bg-indigo-500 text-white px-6 py-3 rounded-lg flex items-center space-x-2"
                  >
                    <ArrowRightIcon className="h-6 w-6" />
                    <span>Interactive Flow Chart</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Table Analysis Demo */}
            <div className="bg-white rounded-xl p-6 mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Detailed Substep Analysis</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">SubStep</th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Issue Count</th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    <tr>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">Customer Greeting</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">3</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        <button
                          onClick={() => showPopup('expand-details')}
                          className="cursor-pointer text-blue-600 hover:text-blue-800 underline"
                        >
                          Expand Details
                        </button>
                      </td>
                    </tr>
                    <tr>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">Issue Resolution</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">1</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        <button
                          onClick={() => showPopup('expand-details')}
                          className="cursor-pointer text-blue-600 hover:text-blue-800 underline"
                        >
                          Expand Details
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Menu Demo */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Mobile Navigation</h3>
              <div className="flex items-center justify-between bg-white rounded-lg p-4 shadow-sm">
                <h4 className="font-semibold text-gray-900">Steps for Customer Service Process</h4>
                <button
                  onClick={() => showPopup('mobile-menu')}
                  className="cursor-pointer transition-colors hover:bg-emerald-700 bg-emerald-600 text-white px-4 py-2 rounded-lg"
                >
                  ☰ Menu
                </button>
              </div>
            </div>

            {/* Feature Explanations */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              <div className="bg-white rounded-xl p-6 border border-blue-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">1</span>
                  </div>
                  <h4 className="text-lg font-bold text-blue-900">Process Selection</h4>
                </div>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Click on any process in the directory to view its detailed workflow,
                  substeps, and associated issues for comprehensive analysis.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-indigo-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">2</span>
                  </div>
                  <h4 className="text-lg font-bold text-indigo-900">Issue Visualization</h4>
                </div>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  Interactive charts display issue patterns across process steps,
                  helping identify bottlenecks and training opportunities.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-purple-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">3</span>
                  </div>
                  <h4 className="text-lg font-bold text-purple-900">Flow Diagrams</h4>
                </div>
                <p className="text-purple-700 text-sm leading-relaxed">
                  Visual flowchart shows process progression with color-coded
                  indicators for bottlenecks and successful completions.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-pink-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-pink-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">4</span>
                  </div>
                  <h4 className="text-lg font-bold text-pink-900">Detailed Drill-down</h4>
                </div>
                <p className="text-pink-700 text-sm leading-relaxed">
                  Expand individual substeps to view specific issues, help responses,
                  and resolution ratings for targeted improvement.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-emerald-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">5</span>
                  </div>
                  <h4 className="text-lg font-bold text-emerald-900">Mobile Optimization</h4>
                </div>
                <p className="text-emerald-700 text-sm leading-relaxed">
                  Responsive design with collapsible navigation menu for seamless
                  access on mobile devices and tablets.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-yellow-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">6</span>
                  </div>
                  <h4 className="text-lg font-bold text-yellow-900">Real-time Updates</h4>
                </div>
                <p className="text-yellow-700 text-sm leading-relaxed">
                  System automatically refreshes data to show current issue counts,
                  completion status, and help response ratings.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Process Analysis Best Practices</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                  <h3 className="text-lg font-bold text-blue-900 mb-3">Analysis Workflow</h3>
                  <ul className="space-y-2 text-sm text-blue-700">
                    <li>• Start with high-level issue charts to identify patterns</li>
                    <li>• Use flow diagrams to understand process dependencies</li>
                    <li>• Drill down into specific substeps with recurring issues</li>
                    <li>• Review help response ratings for quality assessment</li>
                    <li>• Compare completion rates across different processes</li>
                  </ul>
                </div>

                <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
                  <h3 className="text-lg font-bold text-indigo-900 mb-3">Training Identification</h3>
                  <ul className="space-y-2 text-sm text-indigo-700">
                    <li>• Look for substeps with consistently high issue counts</li>
                    <li>• Identify staff members struggling with specific tasks</li>
                    <li>• Review low-rated help responses for improvement areas</li>
                    <li>• Monitor bottleneck patterns over time</li>
                    <li>• Create targeted training programs for problem areas</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                  <h3 className="text-lg font-bold text-purple-900 mb-3">Process Optimization</h3>
                  <ul className="space-y-2 text-sm text-purple-700">
                    <li>• Streamline processes with excessive substeps</li>
                    <li>• Automate repetitive manual tasks where possible</li>
                    <li>• Reorganize workflow based on issue analysis</li>
                    <li>• Update documentation for frequently problematic steps</li>
                    <li>• Implement preventive measures for known bottlenecks</li>
                  </ul>
                </div>

                <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                  <h3 className="text-lg font-bold text-pink-900 mb-3">Performance Monitoring</h3>
                  <ul className="space-y-2 text-sm text-pink-700">
                    <li>• Track issue resolution times and success rates</li>
                    <li>• Monitor staff performance improvements over time</li>
                    <li>• Compare processes across different team members</li>
                    <li>• Identify best practices from high-performing staff</li>
                    <li>• Generate reports for management review</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Popup Components */}
      {activePopup === 'select-process' && (
        <Popup
          title="Select Process"
          description="Click this button to view the detailed workflow, substeps, and issues for the selected process. This opens the full process analysis interface with charts and flow diagrams."
          isOpen={true}
          onClose={closePopup}
        />
      )}
      {activePopup === 'issue-chart' && (
        <Popup
          title="Issue Analytics Chart"
          description="Interactive chart showing the number of issues per task step. Hover over bars to see detailed counts and use this data to identify problematic areas in your processes."
          isOpen={true}
          onClose={closePopup}
        />
      )}
      {activePopup === 'process-flow' && (
        <Popup
          title="Process Flow Visualization"
          description="Interactive flowchart displaying the complete process workflow. Green nodes indicate successful steps, red nodes show bottlenecks. Click and drag to explore the flow."
          isOpen={true}
          onClose={closePopup}
        />
      )}
      {activePopup === 'expand-details' && (
        <Popup
          title="Expand Details"
          description="Click to reveal detailed information about issues, help responses, and ratings for this specific substep. Use this to drill down into specific problems and solutions."
          isOpen={true}
          onClose={closePopup}
        />
      )}
      {activePopup === 'mobile-menu' && (
        <Popup
          title="Mobile Navigation Menu"
          description="On mobile devices, click this menu button to show/hide the process directory sidebar. This provides easy navigation between different processes on smaller screens."
          isOpen={true}
          onClose={closePopup}
        />
      )}
    </div>
  );
}